import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import lowerCase from 'lodash/lowerCase';

import { Datatable } from 'ui';
import { CreateNewPortfolioModal, RequestEsgScoreModal } from 'modules/common/Modals';
import ESGEntitiesFilterModal from 'modules/ESG/ESGEntitiesFilterModal';
import CreditRatingEntitiesFiltersModal from 'modules/creditRating/CreditRatingEntitiesFiltersModal';

import { useSnackbar } from 'shared/hooks/useSnackbar';
import { orderTypes } from 'shared/constants/orderTypes';
import { purchaseTypes } from 'shared/constants/purchaseTypes';
import { selectedItemsToProductsParser, ratingRequestParser } from 'shared/parsers';
import { removeItemsFromPortfolio, putNotifyError } from 'shared/functions';
import { tableRefreshReasonTypes as refreshReasons } from 'shared/constants/tableRefreshReasonTypes';
import { useTable, usePortfolioModals } from 'shared/hooks';
import { postOrderMany } from 'services/orders';
import { exportService, asyncExportService } from 'services/export';
import { requestSentimentScore } from 'services/esg/fetchSentimentSummary';
import { ratingTypes } from 'shared/constants/ratingTypes';
import { portfolioItemIdTypes } from 'shared/constants/portfolio/portfolioItemIdTypes';
import { exportModes } from 'shared/constants/exportModes';
import { dedicatedPortfoliosLabels as esgDedicatedPortfoliosLabels } from 'shared/constants/esg/dedicatedPortfoliosLabels';

import { ESGScoringToolModal, ESGEntityRemovalConfirmationModal } from 'modules/ESG';
import { useFeatures } from 'shared/hooks/useFeatures/useFeatures';
import { shopProductsCategories } from 'modules/shop/shared/constants/shopProductsCategories';
import { ShopRelatedProductsModal } from 'modules/shop/components/ShopRelatedProductsModal';
import { ShopProductLines } from 'modules/shop/shared/constants/shopProductLines';
import { RatingStatus } from 'shared/types/ratingStatus';

import { UpdateEntitiesModal } from 'modules/common/Modals/UpdateEntitiesModal/UpdateEntitiesModal';
import { ItemType } from 'shared/types/portfolios';
import { MenuItem, Select } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import useEntitiesTableColumns from './useEntitiesTableColumns';
import { switchScenarioAction } from '../../../../store/searchResult/searchResultActions';
import { theme } from '../../../../theme';

const useStyles = makeStyles(() => ({
  cstSelect: {
    position: 'absolute',
    top: theme.spacing(3),
    right: theme.spacing(2),
  },
}));

const EntitiesTable = ({
  paginationLimit,
  dataSelector,
  actionCreator,
  actionCreatorParams,
  searchQuery,
  dossierId,
  ratingType,
  onItemsRemoved,
  constantFilter,
  initialFilter,
  initialFilterModalValues,
  filterableIndustries,
  allowEmptyIndustriesFilter,
  renderDatatable,
  extendedExport,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const didUnmount = useRef(false);
  const dispatch = useDispatch();
  const entity = useSelector((state) => state.entity);
  const { success } = useSnackbar();

  const { requestedEntityId = entity.id, isRatingRequestPending, ratingRequestError } = entity;
  const isPortfolioTable = !!dossierId;

  const { esgCompanies } = useFeatures();

  const { singleDossier: dossierData } = useSelector(({ dossier }) => dossier);
  const isDedicatedPortfolio = isPortfolioTable && dossierData && dossierData.isDedicated;
  const isESGScoringTool =
    ratingType === ratingTypes.ESG_IMPACT &&
    isDedicatedPortfolio &&
    lowerCase(esgDedicatedPortfoliosLabels.scoringTool) === lowerCase(dossierData.name);

  const esgContractData = useSelector(({ esgContract }) => esgContract);
  const { contractData } = useSelector(({ creditReview }) => creditReview);

  const { query } = searchQuery;

  const [entitiesFiltersModalOpen, setEntitiesFiltersModalOpen] = useState(false);
  const [ESGScoringToolModalOpen, setESGScoringToolModalOpen] = useState(false);
  const [ESGEntityRemovalConfirmationModalOpen, setESGEntityRemovalConfirmationModalOpen] =
    useState(false);
  const [countriesAndIndustries, setCountriesAndIndustries] = useState();

  const [requestedEntitiesIds, setRequestedEntitiesIds] = useState([]);
  const [requestLoading, setRequestLoading] = useState(false);
  const [selected, setSelected] = useState({ items: {}, count: 0 });
  const [shouldRefresh, setShouldRefresh] = useState({ value: false, reason: null });

  const [dossiersRequest, setDossiersRequest] = useState({ items: {}, count: 0, type: null });
  const [isRequestEsgModalOpen, setRequestEsgModalOpen] = useState(false);

  const [isRelatedProductsModalOpen, setRelatedProductsModalOpen] = useState(false);

  const [cstScenario, setCstScenario] = useState('orderly');

  const onSelectedChange = (selectedItems) => {
    setSelected({ ...selectedItems });
    setDossiersRequest({ ...selectedItems, type: ItemType.SCOPEONEID });
  };

  const toggleEntitiesFiltersModal = () => setEntitiesFiltersModalOpen(!entitiesFiltersModalOpen);
  const toggleESGScoringToolModal = () => setESGScoringToolModalOpen(!ESGScoringToolModalOpen);
  const toggleESGEntityRemovalConfirmationModal = () =>
    setESGEntityRemovalConfirmationModalOpen(!ESGEntityRemovalConfirmationModalOpen);

  const { createPortfolio, addToPortfolio } = usePortfolioModals();

  const [esgProductData] = useState({
    category: shopProductsCategories.esgAnalysis,
    // FIXME: We probably also need to determine here if we should show Tools or Review products based on
    // esgExists field just like in Summary Card. Let's think about best way to do it.
    productLine: ShopProductLines.Tools,
    countriesAndIndustries: [],
  });

  const [creditRatingProductData, setCreditRatingProductData] = useState();

  useEffect(() => {
    setCreditRatingProductData({
      category: shopProductsCategories.creditAnalysis,
      productLine: ShopProductLines.Rating,
      countriesAndIndustries: countriesAndIndustries || [],
    });
  }, [countriesAndIndustries]);

  useEffect(() => {
    if (
      !isRatingRequestPending &&
      ratingRequestError &&
      requestedEntitiesIds.includes(requestedEntityId)
    ) {
      setRequestedEntitiesIds((values) => values.filter((id) => id !== requestedEntityId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRatingRequestPending]);

  useEffect(() => {
    return () => {
      didUnmount.current = true;
    };
  }, []);

  const openRequestEsgModal = (selectedIds) => {
    setRequestedEntitiesIds(selectedIds);
    setRequestEsgModalOpen(true);
  };

  const openRelatedProductsModal = (data) => {
    if (data) {
      setCountriesAndIndustries(data);
    }
    setRelatedProductsModalOpen(true);
  };

  const requestScore = (ids) => {
    if (ratingType === ratingTypes.ESG_IMPACT) {
      openRequestEsgModal(ids);
      return;
    }

    if (ratingType === ratingTypes.SENTIMENT) {
      requestSentimentScore(ids[0]).then(() => dispatch(refreshTable));
    }
  };

  const initialColumns = useEntitiesTableColumns({
    ratingType,
    isPortfolioTable,
    isRequestEnabled:
      (ratingType === ratingTypes.CREDIT_REVIEW &&
        contractData &&
        contractData.cofaceRequestPermission) ||
      (ratingType === ratingTypes.ESG_IMPACT &&
        esgCompanies &&
        esgContractData &&
        esgContractData.esgRequestPermission),
    isUnlockEnabled: ratingType === ratingTypes.ESG_IMPACT && esgCompanies,
    onRequestClick: requestScore,
    onUnlockClick: ratingType !== ratingTypes.CREDIT_REVIEW ? openRelatedProductsModal : null,
    isESGScoringTool,
  });

  const columnsFilterKeysMapping = [
    null,
    null,
    'country',
    'industrySectors',
    'ratingType',
    ['longTermRatingFrom', 'longTermRatingTo'],
    null,
  ];

  const { columns, pagination, onTableChange, onSortChange, refreshTable, setFilter, isFiltered } =
    useTable({
      paginationLimit,
      initialColumns,
      columnsFilterKeysMapping,
      getTableDataAction: actionCreator,
      getTableDataActionParams: actionCreatorParams,
      constantFilter,
      initialFilter,
    });

  const handleEntitiesFiltersModalSubmit = (values) => {
    const {
      countries,
      industrySectors,
      actionType,
      ratingType: ratingTypeFilter,
      longTermRatingFrom,
      longTermRatingTo,
    } = values;

    const newFilters = {
      ...(countries.length && { countries }),
      ...(industrySectors.length !== 0 && { industrySectors }),
      ...(ratingTypeFilter !== null && { ratingType: [ratingTypeFilter] }),
      ...(actionType !== null && { actionType }),
      ...(longTermRatingFrom !== 'D' && { longTermRatingFrom }),
      ...(longTermRatingTo !== 'AAA' && { longTermRatingTo }),
    };

    setFilter(newFilters);
    setShouldRefresh({ value: true, reason: refreshReasons.FILTERS_APPLIED });
    setEntitiesFiltersModalOpen(false);
  };

  const handleESGEntitiesFiltersModalSubmit = (values) => {
    const {
      countries,
      sectors,
      environmentFrom,
      environmentTo,
      socialFrom,
      socialTo,
      governanceFrom,
      governanceTo,
      totalScoreFrom,
      totalScoreTo,
    } = values;

    const newFilters = {
      ...(countries.length && { countries }),
      ...(sectors.length !== 0 && { industries: sectors }),
      ...(environmentFrom !== '0' && { environmentFrom }),
      ...(environmentTo !== '10' && { environmentTo }),
      ...(socialFrom !== '0' && { socialFrom }),
      ...(socialTo !== '10' && { socialTo }),
      ...(governanceFrom !== '0' && { governanceFrom }),
      ...(governanceTo !== '10' && { governanceTo }),
      ...(totalScoreFrom !== '0' && { totalScoreFrom }),
      ...(totalScoreTo !== '10' && { totalScoreTo }),
    };

    setFilter(newFilters);
    setShouldRefresh({ value: true, reason: refreshReasons.FILTERS_APPLIED });
    setEntitiesFiltersModalOpen(false);
  };

  useEffect(() => {
    setRequestLoading(dataSelector.isPending);
  }, [dataSelector.isPending]);

  const extractIdsByType = (items, idType) => {
    return Object.values(items).map((itemData) => {
      const [scopeoneId, esgId, artifactId] = itemData[itemData.length - 1];
      switch (idType) {
        case portfolioItemIdTypes.SCOPEONE_ID:
          return scopeoneId;
        case portfolioItemIdTypes.ESG_ID:
          return esgId;
        case portfolioItemIdTypes.ARTIFACT_ID:
          return artifactId;
        default:
          return undefined;
      }
    });
  };

  const handleRequestButton = (items) => {
    setRequestLoading(true);

    if (ratingType === ratingTypes.ESG_IMPACT) {
      const selectedItems = extractIdsByType(items, portfolioItemIdTypes.ESG_ID);
      setRequestedEntitiesIds(selectedItems);
      setRequestEsgModalOpen(true);
      setRequestLoading(false);
    } else {
      const selectedItems = Object.keys(items);
      const filteredRatingRequest = ratingRequestParser(selectedItems);
      const products = selectedItemsToProductsParser({
        selected: filteredRatingRequest,
        artifactType: orderTypes.ENTITY,
        frequency: 0,
        purchaseType: purchaseTypes.ONE_TIME,
      });

      postOrderMany({ products })
        .then(() => {
          if (!didUnmount.current) {
            setShouldRefresh({ value: true, reason: refreshReasons.PRODUCT_REQUESTED });
            dispatch(refreshTable);
          }
        })
        .finally(() => {
          if (!didUnmount.current) {
            setRequestLoading(false);
          }
        });
    }
  };

  const handleUnlockedButton = (unlocked = true) => {
    setRelatedProductsModalOpen(false);

    if (countriesAndIndustries) {
      setCountriesAndIndustries(null);
    }

    if (unlocked) {
      setShouldRefresh({ value: true, reason: refreshReasons.SUBSCRIPTION_PURCHASED });
      dispatch(refreshTable);
    }
  };

  const closeRequestEsgModal = (requested = true) => {
    if (requested) setShouldRefresh({ value: true, reason: refreshReasons.PRODUCT_REQUESTED });
    setRequestedEntitiesIds([]);
    setRequestEsgModalOpen(false);
    dispatch(refreshTable);
  };

  const dossierItemsIds = (items) => {
    if (ratingType === ratingTypes.ESG_IMPACT) {
      return extractIdsByType(items, portfolioItemIdTypes.ARTIFACT_ID);
      // eslint-disable-next-line no-else-return
    } else {
      return Object.values(items).map((itemData) => {
        const id = itemData[0];
        return Array.isArray(id) ? id[2] : id;
      });
    }
  };

  const exportItemsIds = (items) => {
    if (isESGScoringTool) {
      return extractIdsByType(items, portfolioItemIdTypes.ESG_ID);
      // eslint-disable-next-line no-else-return
    } else {
      return Object.values(items).map((itemData) => {
        const id = itemData[0];
        return ratingType === ratingTypes.CREDIT_RATING || Array.isArray(id) ? id[0] : id;
      });
    }
  };

  const removeItems = (items) => {
    const ids = dossierItemsIds(items);
    setRequestLoading(true);
    removeItemsFromPortfolio({ dossierId, dossierItemsIds: ids, ratingType })
      .then(() => {
        if (!didUnmount.current) {
          setShouldRefresh({ value: true, reason: refreshReasons.DELETED });
          dispatch(refreshTable);
        }
      })
      .finally(() => {
        if (!didUnmount.current) {
          setRequestLoading(false);
          onItemsRemoved();
        }
      });
  };

  const downloadErrorHandler = () => {
    putNotifyError(t('ENTITY.FAILED_TO_EXPORT_ENTITIES'));
  };

  const onExportSuccess = (message) => {
    success(t('TOOLBAR.EXPORT_STARTED'));
  };

  const exportToExcel = (items) => {
    exportService({
      items: exportItemsIds(items),
      type: orderTypes.ENTITY,
      onError: downloadErrorHandler,
      ratingType,
      mode: extendedExport ? exportModes.EXTENDED : exportModes.BASIC,
    });
  };

  const asyncExportToExcel = (items) => {
    asyncExportService({
      items: exportItemsIds(items),
      type: orderTypes.ENTITY,
      onError: downloadErrorHandler,
      onSuccess: onExportSuccess,
      ratingType,
      mode: exportModes.BASIC,
    });
  };

  const asyncExtendedExportToExcel = (items) => {
    asyncExportService({
      items: exportItemsIds(items),
      onError: downloadErrorHandler,
      onSuccess: onExportSuccess,
      ratingType,
      mode: exportModes.EXTENDED,
    });
  };

  const toolbarData = [
    {
      text: t('TOOLBAR.REQUEST_SELECTED'),
      handler: handleRequestButton,
      disabled:
        !selected.count ||
        (ratingType === ratingTypes.CREDIT_REVIEW &&
          (!contractData || !contractData.cofaceRequestPermission)) ||
        (ratingType === ratingTypes.ESG_IMPACT &&
          /* Extracts status from the 'name' column  */
          (Object.values(selected.items).some((cols) => cols[1][1] !== RatingStatus.TO_DO) ||
            (esgCompanies && (!esgContractData || !esgContractData.esgRequestPermission)))),
      hidden:
        !selected.count ||
        (ratingType === ratingTypes.ESG_IMPACT && !isESGScoringTool) ||
        ratingTypes.SENTIMENT,
      data: selected.items,
    },
    {
      text: t(isPortfolioTable ? 'TOOLBAR.COPY_TO_PORTFOLIO' : 'TOOLBAR.ADD_TO_PORTFOLIO'),
      handler: addToPortfolio.show,
      hidden: !selected.count,
    },
    {
      text: t('TOOLBAR.EXPORT_TO_EXCEL'),
      handler: exportToExcel,
      data: selected.items,
      hidden: !selected.count || ratingType === ratingTypes.ESG_IMPACT || ratingTypes.SENTIMENT,
    },
    {
      text: t('TOOLBAR.EXPORT_VIEW_TO_EXCEL'),
      handler: asyncExportToExcel,
      data: selected.items,
      hidden: !selected.count || ratingType !== ratingTypes.ESG_IMPACT || ratingTypes.SENTIMENT,
    },
    {
      text: t('TOOLBAR.EXPORT_FULL_DATA_TO_EXCEL'),
      handler: asyncExtendedExportToExcel,
      data: selected.items,
      hidden: !selected.count || ratingType !== ratingTypes.ESG_IMPACT || ratingTypes.SENTIMENT,
    },
    { text: t('TOOLBAR.SHARE'), hidden: true },
    {
      text: t('TOOLBAR.DELETE'),
      handler: isESGScoringTool ? toggleESGEntityRemovalConfirmationModal : removeItems,
      data: selected.items,
      hidden: !isPortfolioTable || !selected.count,
    },
  ];

  const customButtonLabel = isESGScoringTool ? t('CREATE_COMPANIES') : undefined;
  const onCustomButtonClick = isESGScoringTool ? toggleESGScoringToolModal : undefined;

  const onESGScoringToolUploadFinished = () => {
    setShouldRefresh({ value: true, reason: refreshReasons.UPDATED });
    dispatch(refreshTable);
  };

  const onESGEntityRemovalConfirm = () => {
    toggleESGEntityRemovalConfirmationModal();
    removeItems(selected.items);
  };

  const handleCstChange = (v) => {
    setCstScenario(v);
    dispatch(switchScenarioAction({ scenario: v }));
  };

  const shouldShowFilters = () => {
    if (ratingType === ratingTypes.CLIMATE_STRESS_TEST) {
      return false;
    }
    return !isPortfolioTable && ratingType !== ratingTypes.SENTIMENT;
  };

  return (
    <>
      <Datatable
        loading={requestLoading || !renderDatatable}
        columns={columns}
        pagination={pagination}
        data={renderDatatable ? dataSelector.data : []}
        tableToolbar={
          ratingType === ratingTypes.CLIMATE_STRESS_TEST && (
            <Select
              size="small"
              className={classes.cstSelect}
              value={cstScenario}
              onChange={(e) => handleCstChange(e.target.value)}
            >
              <MenuItem key="orderly" value="orderly">
                {t('Orderly')}
              </MenuItem>
              <MenuItem key="disorderly" value="disorderly">
                {t('Disorderly')}
              </MenuItem>
              <MenuItem key="hotHouse" value="hotHouse">
                {t('Hot House')}
              </MenuItem>
            </Select>
          )
        }
        total={dataSelector.total}
        onSelectedChange={onSelectedChange}
        onTableChange={onTableChange}
        onSortChange={onSortChange}
        toolbarData={toolbarData}
        isFiltered={isFiltered}
        onApplyFiltersClick={() => setEntitiesFiltersModalOpen(true)}
        shouldRefresh={shouldRefresh}
        showFilters={shouldShowFilters()}
        onCustomButtonClick={onCustomButtonClick}
        customButtonLabel={customButtonLabel}
        removeShadow
      />
      {ratingType === ratingTypes.CREDIT_RATING && (
        <CreditRatingEntitiesFiltersModal
          open={entitiesFiltersModalOpen}
          onClose={toggleEntitiesFiltersModal}
          onSubmit={handleEntitiesFiltersModalSubmit}
          searchQuery={query}
          initialFilter={initialFilterModalValues}
          filterableIndustries={filterableIndustries}
          allowEmptyIndustriesFilter={allowEmptyIndustriesFilter}
        />
      )}
      {(ratingType === ratingTypes.ESG_IMPACT || ratingType === ratingTypes.SENTIMENT) && (
        <ESGEntitiesFilterModal
          open={entitiesFiltersModalOpen}
          onClose={toggleEntitiesFiltersModal}
          onSubmit={handleESGEntitiesFiltersModalSubmit}
          searchQuery={query}
          filterableIndustries={filterableIndustries}
          initialFilter={initialFilterModalValues}
        />
      )}
      {esgCompanies && isESGScoringTool && (
        <>
          <ESGScoringToolModal
            open={ESGScoringToolModalOpen}
            onClose={toggleESGScoringToolModal}
            onUploaded={onESGScoringToolUploadFinished}
          />
          <ESGEntityRemovalConfirmationModal
            open={ESGEntityRemovalConfirmationModalOpen}
            onClose={toggleESGEntityRemovalConfirmationModal}
            onConfirm={onESGEntityRemovalConfirm}
          />
        </>
      )}
      {ratingType === ratingTypes.ESG_IMPACT && (
        <RequestEsgScoreModal
          open={isRequestEsgModalOpen}
          onClose={closeRequestEsgModal}
          selectIds={requestedEntitiesIds}
        />
      )}
      <UpdateEntitiesModal
        open={addToPortfolio.isOpen}
        onClose={addToPortfolio.close}
        onClickCreateNew={createPortfolio.show}
        itemsData={dossiersRequest}
        ratingType={ratingType}
        itemType="ENTITY"
      />
      <CreateNewPortfolioModal
        open={createPortfolio.isOpen}
        onClose={createPortfolio.close}
        itemsData={dossiersRequest}
        itemType="ENTITY"
      />
      {isRelatedProductsModalOpen &&
        esgProductData &&
        (ratingType === ratingTypes.ESG_IMPACT || ratingType === ratingTypes.SENTIMENT) && (
          <ShopRelatedProductsModal
            data={esgProductData}
            open={isRelatedProductsModalOpen}
            onClose={handleUnlockedButton}
            onConfirm={handleUnlockedButton}
            module={ratingType}
          />
        )}
      {isRelatedProductsModalOpen &&
        creditRatingProductData &&
        ratingType === ratingTypes.CREDIT_RATING && (
          <ShopRelatedProductsModal
            data={creditRatingProductData}
            open={isRelatedProductsModalOpen}
            onClose={handleUnlockedButton}
            onConfirm={handleUnlockedButton}
          />
        )}
    </>
  );
};

EntitiesTable.defaultProps = {
  actionCreatorParams: {},
  searchQuery: {},
  dossierId: null,
  onItemsRemoved: () => null,
  constantFilter: {},
  initialFilter: {},
  initialFilterModalValues: {},
  filterableIndustries: null,
  allowEmptyIndustriesFilter: true,
  ratingType: ratingTypes.CREDIT_RATING,
  renderDatatable: true,
  paginationLimit: null,
  extendedExport: false,
};

EntitiesTable.propTypes = {
  dataSelector: PropTypes.shape({
    isPending: PropTypes.bool.isRequired,
    total: PropTypes.number.isRequired,
    data: PropTypes.arrayOf(PropTypes.any).isRequired,
  }).isRequired,
  actionCreator: PropTypes.func.isRequired,
  actionCreatorParams: PropTypes.object,
  searchQuery: PropTypes.shape({
    query: PropTypes.string,
    country: PropTypes.arrayOf(PropTypes.string),
    industry: PropTypes.arrayOf(PropTypes.string),
    ratingType: PropTypes.string,
  }),
  dossierId: PropTypes.string,
  onItemsRemoved: PropTypes.func,
  constantFilter: PropTypes.object,
  initialFilter: PropTypes.object,
  initialFilterModalValues: PropTypes.object,
  filterableIndustries: PropTypes.arrayOf(PropTypes.object),
  allowEmptyIndustriesFilter: PropTypes.bool,
  ratingType: PropTypes.oneOf(Object.values(ratingTypes)),
  renderDatatable: PropTypes.bool,
  paginationLimit: PropTypes.number,
  extendedExport: PropTypes.bool,
};

export default EntitiesTable;
